import React, { useEffect, Suspense } from 'react';
import { Switch, withRouter, Route } from 'react-router-dom';
import { compose, prop } from 'ramda';
import { AsyncBookingList } from 'pages/async';
import { usePageVisibility } from 'react-page-visibility';
import { getAppRoutes } from 'routing';
import Layout from 'components/Layout';
import { useScrollToTop, useEffectBoundary } from 'effects';
import { withAuthentication, withUser } from 'hoc';
import connect from './App.state';
import AuthRoute from 'containers/AuthRoute';
import { EUserType } from 'services/BackendApi';
import { ENVIRONMENT, COGNITO_ENABLE_FLOW, APP_ROUTING_PATHS } from 'config';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { useSelector } from 'react-redux';
import { getBootstrapErrorSelector } from 'store/modules/bootstrap/selectors';
import { getBearerTokenFromLocalStorage } from 'services/tokenLocalStorage';
import { noop } from 'utils';
import { BOOKING_BUILDER_V3_PATH } from 'utils/bookingBuilderPath';
import Notifications from 'components/Notifications';
import {
  AsyncFastSearch,
  AsyncRatesImport,
  AsyncProductsImport,
  AsyncStaticRatesAvailabilityImport,
  AsyncOfferRouting,
  AsyncAnnouncementsRouting,
  AsyncHotelCollectionRouting,
  AsyncBookingManagerRouting,
  AsyncRateBreakRouting,
  AsyncTravelCompaniesRouting,
  AsyncTravelAgentsRouting,
  AsyncInternalUsersRouting,
  AsyncStatementRouting,
  AsyncBookingBuilderV1,
  AsyncBookingBuilderV3,
  AsyncProposalsRouting,
  AsyncCredentialsInfoRouting,
  AsyncDashboard,
  AsyncLanding,
  AsyncInventoryWorkspacesHomepage,
  AsyncLiveRatesRouting,
  AsyncCognitoCallback,
  AsyncCognitoFlowError,
  AsyncNotFound,
  AsyncAncillarySuppliersRouting,
  AsyncAncillaryActivitiesRouting,
  AsyncAncillaryTransfersRouting,
  AsyncAncillaryGroundServicesRouting,
  AsyncBookingConfirmationRouting,
  AsyncTaskManagementRouting,
  AsyncRecurringTaskManagementRouting,
  AsyncGroundOperation,
  AsyncBasketRouting,
  AsyncHotelAdminRouting,
  AsyncCRMRouting,
  AsyncContentsAdminRouting,
  AsyncContentMarketingRouting,
  AsyncSettings,
  AsyncSharedLinkRouting,
  AsyncOtpRouting,
  AsyncItineraryPageRouting,
  AsyncVoucherPageRouting,
  AsyncChatPageRouting,
  AsyncReviewPageRouting,
  AsyncKnowledgeBaseRouting,
  AsyncVccRouting,
  AsyncBankTransactionsRouting,
  AsyncConversionRatesPage,
  AsyncPaymentMethodsPage,
  AsyncPaymentMethodCreatePage,
  AsyncPaymentMethodEditPage,
  AsyncSuppliersReconciliationPage,
  AsyncPurchaseOrderDetailPage,
  AsyncInvoiceUploadsPage,
  AsyncInvoiceUploadDetailPage,
  AsyncInvoiceUploadEditPage,
  AsyncPayrunsPage,
  AsyncPayrunDetailPage,
  AsyncPayrunSummaryPage,
  AsyncPaymentNotificationsPage,
  AsyncRechartsDemo,
} from './async';
import { SnackbarProvider } from 'notistack';
const ANCHOR_ORIGIN = Object.freeze({
  vertical: 'bottom',
  horizontal: 'left',
});

import { ErrorBoundary } from 'ui/ErrorBoundary/ErrorBoundary';
import { Loader } from 'components/Loader/Loader';
import withDynamicParameters from 'hoc/withDynamicParameters/withDynamicParameters';

export const App = ({
  location: { pathname },
  user,
  resetStatuses,
  pageChange,
  bootstrapInitRequestAction,
  bootstrapAppRequestAction,
  visibilityChange,
  isAppVersionDeprecated = false,
  isAuthenticated,
  setToken = noop,
}) => {
  const { dynamicParameters, dynamicParametersAreEmpty } = useDynamicParameters();
  const bootstrapError = useSelector(getBootstrapErrorSelector);

  // Scroll to top on path change
  useScrollToTop(pathname);

  useEffect(() => {
    // App can load at the beginning or at any point of the flow
    // Either user is logged in and can throw BOOTSTRAP_APP_REQUEST
    // Either is not yet then when AUTH_LOG_IN_SUCCESS happens same saga will be triggered
    const token = getBearerTokenFromLocalStorage();
    if (!!token.value && !token.expired) {
      // Normal bootstrap flow for app load at any point
      bootstrapAppRequestAction();
    } else {
      // First Cognito flow, then normal bootstrap flow
      bootstrapInitRequestAction();
    }
  }, []);

  useEffectBoundary(() => {
    pageChange(pathname);

    // Reset statuses on page change
    resetStatuses();
  }, [pathname]);

  const visible = usePageVisibility();

  useEffect(() => {
    visibilityChange(visible);
  }, [visibilityChange, visible]);

  if (bootstrapError) {
    return (
      <Layout isAppVersionDeprecated={isAppVersionDeprecated}>
        <div className="bootstrap-error mt-[100px]">
          <ErrorBar message="Service temporary unavailable. Please try again later." />
        </div>
      </Layout>
    );
  }

  if (dynamicParametersAreEmpty) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <LoadingBar />
      </div>
    );
  }

  if (
    ENVIRONMENT !== 'local' &&
    !isAuthenticated &&
    window.location.pathname === '/' &&
    dynamicParameters.MARKETING_APP_URL
  ) {
    window.location.replace(dynamicParameters.MARKETING_APP_URL);
  }

  return (
    <Layout isAppVersionDeprecated={isAppVersionDeprecated}>
      <Suspense fallback={<Loader />}>
        <Switch>
          {/* If inventory is ON, all users get the new homepage... */}
          <AuthRoute
            path="/"
            exact
            component={AsyncInventoryWorkspacesHomepage}
            allow={[
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.RL,
              EUserType.SR,
              EUserType.TA,
              EUserType.SALES,
              EUserType.MARKETING,
              EUserType.GROUND_OPERATOR,
            ]}
          />
          {/* ...and everyone gets a new "filters" page */}
          <AuthRoute
            path="/filters"
            exact
            component={
              user && (user.type === EUserType.SR || user.type === EUserType.ADMIN) ? AsyncDashboard : AsyncLanding
            }
            allow={[EUserType.TA, EUserType.ADMIN, EUserType.FINANCE, EUserType.RL, EUserType.SR]}
          />

          <AuthRoute
            path="/search/beta"
            exact
            component={AsyncFastSearch}
            allow={[EUserType.TA, EUserType.SR, EUserType.ADMIN]}
          />
          <AuthRoute path="/booking-manager" component={AsyncBookingManagerRouting} />
          <AuthRoute allow={[EUserType.RL, EUserType.ADMIN]} path="/offers" component={AsyncOfferRouting} />
          <AuthRoute
            allow={[EUserType.RL, EUserType.ADMIN, EUserType.FINANCE]}
            path="/announcements"
            component={AsyncAnnouncementsRouting}
          />
          <AuthRoute
            allow={[
              EUserType.RL,
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.SALES,
              EUserType.SR,
              EUserType.TA,
              EUserType.MARKETING,
            ]}
            path="/hotels-collection"
            component={AsyncHotelCollectionRouting}
          />
          <AuthRoute allow={[EUserType.RL, EUserType.ADMIN]} exact path="/rates/import" component={AsyncRatesImport} />
          <AuthRoute
            allow={[EUserType.RL, EUserType.ADMIN]}
            exact
            path="/products/import"
            component={AsyncProductsImport}
          />
          <AuthRoute
            allow={[EUserType.RL, EUserType.ADMIN]}
            exact
            path="/static-rates-availability/import"
            component={AsyncStaticRatesAvailabilityImport}
          />
          <AuthRoute
            allow={[EUserType.TA, EUserType.SR, EUserType.RL, EUserType.FINANCE, EUserType.ADMIN, EUserType.SALES]}
            exact
            path="/bookings"
            component={AsyncBookingList}
          />
          <AuthRoute allow={[EUserType.RL, EUserType.ADMIN]} path="/rate-breaks" component={AsyncRateBreakRouting} />
          {dynamicParameters.ENABLE_ANCILLARY_SUPPLIERS_IMPORT && !dynamicParameters.ENABLE_CRM_WORKSPACE_TEMP && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/suppliers"
              component={AsyncAncillarySuppliersRouting}
            />
          )}
          {dynamicParameters.ENABLE_ANCILLARY_ACTIVITIES_IMPORT && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/activities"
              component={AsyncAncillaryActivitiesRouting}
            />
          )}
          {dynamicParameters.ENABLE_ANCILLARY_TRANSFERS_IMPORT && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/transfers"
              component={AsyncAncillaryTransfersRouting}
            />
          )}
          {dynamicParameters.ENABLE_ANCILLARY_GROUND_SERVICES_IMPORT && (
            <AuthRoute
              allow={[EUserType.RL, EUserType.ADMIN]}
              path="/ground-services"
              component={AsyncAncillaryGroundServicesRouting}
            />
          )}
          {/* Admin, SR, Finance --> Can see one TC's settings, but SR and F cannot see the list for all TCs */}
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR, EUserType.FINANCE]}
            path="/travel-companies"
            component={AsyncTravelCompaniesRouting}
          />
          <AuthRoute allow={[EUserType.ADMIN]} path="/users" component={AsyncInternalUsersRouting} />
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR]}
            path="/travel-agents"
            component={AsyncTravelAgentsRouting}
          />
          <AuthRoute allow={[EUserType.ADMIN]} path="/users" component={AsyncInternalUsersRouting} />
          <AuthRoute allow={[EUserType.ADMIN, EUserType.FINANCE]} path="/vcc" component={AsyncVccRouting} />
          {dynamicParameters.ENABLE_BANK_TRANSACTIONS_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR]}
              path="/finance/bank-transactions"
              component={AsyncBankTransactionsRouting}
            />
          )}
          {dynamicParameters.ENABLE_FX_MATRIX_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.RL]}
              path="/finance/conversion-rates"
              component={AsyncConversionRatesPage}
            />
          )}
          {dynamicParameters.ENABLE_PAYMENT_METHODS_ADMIN_PANEL_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              exact
              path="/finance/payment-methods/create"
              component={AsyncPaymentMethodCreatePage}
            />
          )}
          {dynamicParameters.ENABLE_PAYMENT_METHODS_ADMIN_PANEL_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              exact
              path="/finance/payment-methods/:code/edit"
              component={AsyncPaymentMethodEditPage}
            />
          )}
          {dynamicParameters.ENABLE_PAYMENT_METHODS_ADMIN_PANEL_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/payment-methods"
              component={AsyncPaymentMethodsPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/suppliers-reconciliation/detail"
              component={AsyncPurchaseOrderDetailPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/suppliers-reconciliation"
              component={AsyncSuppliersReconciliationPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/invoice-uploads/:uuid/edit"
              component={AsyncInvoiceUploadEditPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/invoice-uploads/:uuid"
              component={AsyncInvoiceUploadDetailPage}
            />
          )}
          {dynamicParameters.ENABLE_BASIC_PO_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.FINANCE]}
              path="/finance/invoice-uploads"
              component={AsyncInvoiceUploadsPage}
            />
          )}
          {/* Notifications must be registered before the non-exact /summary and :uuid routes or it would never match. */}
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE]}
            path="/finance/payruns/:uuid/summary/notifications"
            component={AsyncPaymentNotificationsPage}
          />
          {/* Summary must be registered before the non-exact :uuid route or it would never match. */}
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE]}
            path="/finance/payruns/:uuid/summary"
            component={AsyncPayrunSummaryPage}
          />
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE]}
            path="/finance/payruns/:uuid"
            component={AsyncPayrunDetailPage}
          />
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE]}
            path="/finance/payruns"
            component={AsyncPayrunsPage}
          />
          <AuthRoute
            allow={
              user?.companyTaManager || user?.taCanReadFinance
                ? [EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.TA]
                : [EUserType.ADMIN, EUserType.FINANCE, EUserType.SR]
            }
            path="/statement"
            component={AsyncStatementRouting}
          />
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
            path="/booking-builder"
            component={AsyncBookingBuilderV1}
          />
          {dynamicParameters.ENABLE_NEW_BB_DESIGN_TEMP && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
              path={BOOKING_BUILDER_V3_PATH}
              component={AsyncBookingBuilderV3}
            />
          )}
          <AuthRoute
            allow={[EUserType.TA, EUserType.SR, EUserType.ADMIN]}
            path="/proposals-v2"
            component={AsyncProposalsRouting}
          />
          {dynamicParameters.ENABLE_CHAT_NEW_PAGE_TEMP && (
            <AuthRoute
              allow={
                dynamicParameters.ENABLE_CHAT_NEW_PAGE_TA_TEMP
                  ? [EUserType.SR, EUserType.ADMIN, EUserType.TA]
                  : [EUserType.SR, EUserType.ADMIN]
              }
              path="/pure-ai"
              component={AsyncChatPageRouting}
            />
          )}
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
            path="/booking-confirmation"
            component={AsyncBookingConfirmationRouting}
          />
          {dynamicParameters.ENABLE_BASKET && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
              path={APP_ROUTING_PATHS.basket.home}
              component={AsyncBasketRouting}
            />
          )}
          {dynamicParameters.ENABLE_BASKET && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA]}
              path={APP_ROUTING_PATHS.basket.edit}
              component={AsyncBookingBuilderV1}
            />
          )}
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.SR]}
            path="/ta-history"
            component={AsyncCredentialsInfoRouting}
          />
          <AuthRoute allow={[EUserType.RL, EUserType.ADMIN]} path="/live-rates" component={AsyncLiveRatesRouting} />
          {COGNITO_ENABLE_FLOW && <Route path="/login-error" component={AsyncCognitoFlowError} />}
          {COGNITO_ENABLE_FLOW && <Route path="/cognito-callback" component={AsyncCognitoCallback} />}
          {COGNITO_ENABLE_FLOW && <Route path="/login" component={AsyncNotFound} />}
          {COGNITO_ENABLE_FLOW && <Route path="/settings/password" component={AsyncNotFound} />}
          {((user?.type === EUserType.TA && dynamicParameters.ENABLE_OPERATIONS_WORKSPACE_TA) ||
            (user?.type !== EUserType.TA && dynamicParameters.ENABLE_OPERATIONS_WORKSPACE_INTERNAL_USERS)) && (
            <AuthRoute
              allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA, EUserType.FINANCE, EUserType.RL, EUserType.SALES]}
              path="/tasks"
              component={AsyncTaskManagementRouting}
            />
          )}
          {dynamicParameters.TMS_RECURRING_TASKS_TEMP &&
            ((user?.type === EUserType.TA && dynamicParameters.ENABLE_OPERATIONS_WORKSPACE_TA) ||
              (user?.type !== EUserType.TA && dynamicParameters.ENABLE_OPERATIONS_WORKSPACE_INTERNAL_USERS)) && (
              <AuthRoute
                allow={[EUserType.ADMIN, EUserType.SR, EUserType.TA, EUserType.FINANCE, EUserType.RL]}
                path="/recurring-tasks"
                component={AsyncRecurringTaskManagementRouting}
              />
            )}
          {user?.type !== EUserType.TA &&
            dynamicParameters.ENABLE_OPERATIONS_WORKSPACE_INTERNAL_USERS &&
            dynamicParameters.ENABLE_GT_OPERATION && (
              <AuthRoute
                allow={[EUserType.ADMIN, EUserType.SR, EUserType.GROUND_OPERATOR]}
                path="/ground-operation"
                component={AsyncGroundOperation}
              />
            )}

          <AuthRoute allow={[EUserType.ADMIN, EUserType.RL]} path="/hotel-admin" component={AsyncHotelAdminRouting} />

          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.RL]}
            path="/content-admin"
            component={AsyncContentsAdminRouting}
          />

          <Route path="/content" component={AsyncContentMarketingRouting} />

          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.SALES]}
            path="/crm"
            component={AsyncCRMRouting}
          />

          <AuthRoute
            allow={[
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.MARKETING,
              EUserType.RL,
              EUserType.SALES,
              EUserType.SR,
              EUserType.TA,
            ]}
            path="/knowledgebase"
            component={AsyncKnowledgeBaseRouting}
          />

          {/* this does the /settings/profile-details and /settings/company-details */}
          <AuthRoute
            allow={[
              EUserType.ADMIN,
              EUserType.FINANCE,
              EUserType.SR,
              EUserType.SALES,
              EUserType.TA,
              EUserType.MARKETING,
              EUserType.GROUND_OPERATOR,
            ]}
            path="/settings/:section?"
            component={AsyncSettings}
          />

          {dynamicParameters.ENABLE_BOOKING_ITINERARY_TEMP && (
            <Route path={APP_ROUTING_PATHS.itinerary} component={AsyncItineraryPageRouting} />
          )}

          <Route path={APP_ROUTING_PATHS.reviews} component={AsyncReviewPageRouting} />

          <Route path={APP_ROUTING_PATHS.voucher} component={AsyncVoucherPageRouting} />
          <Route path={APP_ROUTING_PATHS.sharedLink} component={AsyncSharedLinkRouting} />
          <Route path={APP_ROUTING_PATHS.otp} component={AsyncOtpRouting} />
          <AuthRoute
            allow={[EUserType.ADMIN, EUserType.FINANCE, EUserType.SR, EUserType.SALES, EUserType.MARKETING]}
            path="/demo/recharts"
            component={AsyncRechartsDemo}
          />

          {...getAppRoutes(prop('type', user))}
        </Switch>
      </Suspense>

      <div id="portal-header"></div>
      <div id="portal-tooltip"></div>
      <div id="portal-modal"></div>
      <div id="portal-overlay"></div>
      <div id="portal-bottom-drawer"></div>
      <div id="portal-toast-notification"></div>

      <div className="z-[999999]">
        <SnackbarProvider anchorOrigin={ANCHOR_ORIGIN} className="z-[999999]">
          <Notifications />
        </SnackbarProvider>
      </div>
    </Layout>
  );
};

export default compose(ErrorBoundary, withRouter, withUser, withAuthentication, withDynamicParameters, connect)(App);
